import { BrowserModule } from '@angular/platform-browser';
import { LOCALE_ID, NgModule, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { LocationStrategy, HashLocationStrategy, DecimalPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { ModalModule } from 'ngx-bootstrap/modal';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';
import { DatePipe, registerLocaleData } from '@angular/common';

import {
    AppAsideModule,
    AppBreadcrumbModule,
    AppHeaderModule,
    AppFooterModule,
    AppSidebarModule,
} from '@coreui/angular';
import localeES from '@angular/common/locales/es';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { ChartsModule } from 'ng2-charts';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { OwlModule } from 'ngx-owl-carousel';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSliderModule } from '@angular/material/slider';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ImageViewerModule } from 'ngx-image-viewer';
import { DateValueAccessorModule } from 'angular-date-value-accessor';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { esLocale } from 'ngx-bootstrap/locale';
defineLocale('es', esLocale);

import { AngularDualListBoxModule } from 'angular-dual-listbox';
import { AutocompleteLibModule } from 'angular-ng-autocomplete';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing';
import { AppInfo } from './common/model/dictionary/AppInfo';
import { HomeComponent } from './views/home/home.component';

import { DefaultLayoutComponent } from './containers';
import { P404Component } from './views/error/404.component';
import { P500Component } from './views/error/500.component';
import { LoginComponent } from './views/login/login.component';

import { ReportComponent } from './views/task/report/report.component';
import { ReportStoreComponent } from './views/task/report/reportstore.component';
import { ReportStoreInstructionComponent } from './views/task/report/reportstoreinstruction.component';
import { ReportStorePriceChangeComponent } from './views/task/report/reportstorepricechange.component';
import { ReportStorePriceAdvanceComponent } from './views/task/report/reportstorepriceadvance.component';
import { ReportStoreInventoryComponent } from './views/task/report/reportstoreinventory.component';

// Components Routing
import { Strings } from './common/language/strings';
import { ActivityStoreFacade } from './common/facade/ActivityStoreFacade';
import { CatalogDetFacade } from './common/facade/CatalogDetFacade';
import { BlobStorageFacade } from './common/facade/BlobStorageFacade';
import { StoreFacade } from './common/facade/StoreFacade';
import { EmployeeFacade } from './common/facade/EmployeeFacade';
import { ChecklistFacade } from './common/facade/ChecklistFacade';
import { ApplicationFacade } from './common/facade/ApplicationFacade';
import { StoreScheduleFacade } from './common/facade/StoreScheduleFacade';
import { SecurityFacade } from './common/facade/SecurityFacade';
import { ConfigurationFacade } from './common/facade/ConfigurationFacade';
import { PositionFacade } from './common/facade/PositionFacade';
import { PositionStoreFacade } from './common/facade/PositionStoreFacade';
import { ShipmentVarietySizeFacade } from './common/facade/ShipmentVarietySizeFacade';
import { ShipmentManifestFacade } from './common/facade/ShipmentManifestFacade';
import { ShipmentASNFacade as ShipmentAsnService } from './common/facade/ShipmentASN.service';
import { ShipmentVarietySizeASNService } from './common/facade/ShipmentVarietySizeASN.service';
import { InventoryEntryDetFacade } from './common/facade/InventoryEntryDetFacade';
import { DashboardFacade } from './common/facade/DashboardFacade';
import { DeviceFacade } from './common/facade/DeviceFacade';
import { ReturnRequestFacade } from './common/facade/ReturnRequestFacade';
import { ReturnStoreFacade } from './common/facade/ReturnStoreFacade';
import { CampaignCalendarFacade } from './common/facade/CampaignCalendarFacade';
import { RegionalStoreStructureFacade } from './common/facade/RegionalStoreStructureFacade';
import { ImageCollectorFacade } from './common/facade/ImageCollectorFacade';
import { TenantFacade } from './common/facade/TenantFacade';
import { PriceLabelSectionFacade } from './common/facade/PriceLabelSectionFacade';
import { ReturnConfigFacade } from './common/facade/ReturnConfigFacade';
import { TaskFacade } from './common/facade/TaskFacade';
import { UnsoldItemSizeFacade } from './common/facade/UnsoldItemSizeFacade';

import { LoaderComponent } from './common/presentation/loader/loader.component';
import { AlertModule } from 'ngx-bootstrap/alert';
import { HeaderNavComponent } from './views/header-nav/header-nav.component';
import { AdminComponent } from './views/adminChecklist/admin.component';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { ChecklistComponent } from './views/adminChecklist/checklist/checklist.component';
import { OrganizationChartComponent } from './views/configuration/organization-chart/organization-chart.component';
import { OrganizationChartDetailComponent } from './views/configuration/organization-chart-detail/organization-chart-detail.component';
import { ZoneAdminComponent } from './views/configuration/zone-admin/zone-admin.component';
// Interceptors
import { AuthInterceptorService } from './auth-interceptor.service';

import { ReceptionsComponent } from './views/inventory/receptions/receptions.component';
import { ReceptionsDetailComponent } from './views/inventory/receptions-detail/receptions-detail.component';
import { PackageReceptionFacade } from './common/facade/PackageReceptionFacade';
import { ShipmentsComponent } from './views/inventory/shipments/shipments.component';
import { ShipmentsAsnComponent } from './views/inventory/shipments-asn/shipments-asn.component';
import { shipmentsAsnDetailComponent } from './views/inventory/shipments-asn-detail/shipments-asn-detail.component';
import { ShipmentsAsnVarietyDetailComponent } from './views/inventory/shipments-asn-variety-detail/shipments-asn-variety-detail.component';
import { ShipmentFacade } from './common/facade/ShipmentFacade';
import { InventoryEntryComponent } from './views/inventory/inventory-entry/inventory-entry.component';
import { PackageReceptionComponent } from './views/inventory/package-reception/package-reception.component';
import { StoresComponent } from './views/configuration/stores/stores.component';
import { InventoryEntryDetComponent } from './views/inventory/inventory-entry-det/inventory-entry-det.component';
import { EmployeeAdminComponent } from './views/configuration/employee-admin/employee-admin.component';
import { DeviceAdminComponent } from './views/configuration/device-admin/device-admin.component';
import { ExcelUtility } from './common/util/ExcelUtility';
import { TemplateComponent } from './views/template/template.component';
import { DateFormatPipe } from './common/util/date-format.pipe';
import { ActionDetailComponent } from './views/instruction-action/action-detail/action-detail.component';
import { ActionStoreComponent } from './views/instruction-action/action-store/action-store.component';

import { ActivityStoreInstructionActionFacade } from './common/facade/ActivityStoreInstructionActionFacade';
import { TransferCorporateComponent } from './views/transfer/transfer-corporate/transfer-corporate.component';
import { TransferStoreComponent } from './views/transfer/transfer-store/transfer-store.component';
import { TransferRequestFacade } from './common/facade/TransferRequestFacade';
// Feature 46570 - Carga de Traspasos de ROi
import { TransferRequestBulkFacade } from './common/facade/TransferRequestBulkFacade';
import { TransferBulkSignalRService } from './common/service/TransferBulkSignalRService';
import { WhatsNewFacade } from './common/facade/WhatsNewFacade';
import { OrganizationFacade } from './common/facade/OrganizationFacade';
import { TransferCorporateDetailComponent } from './views/transfer/transfer-corporate-detail/transfer-corporate-detail.component';
import { TransferStoreFacade } from './common/facade/TransferStoreFacade';
import { TransferStoreDetailComponent } from './views/transfer/transfer-store-detail/transfer-store-detail.component';
import { TransferStoreEntryComponent } from './views/transfer/transfer-store-entry/transfer-store-entry.component';
import { TransferStoreEntryDetailComponent } from './views/transfer/transfer-store-entry-detail/transfer-store-entry-detail.component';
import { InventoryCountComponent } from './views/inventory/inventory-count/inventory-count.component';
import { InventoryCountFacade } from './common/facade/InventoryCountFacade';
import { InventoryCountCreateComponent } from './views/inventory/inventory-count-create/inventory-count-create.component';
import { InventoryCountDetailComponent } from './views/inventory/inventory-count-detail/inventory-count-detail.component';
import { InventoryCountTagDetFacade } from './common/facade/InventoryCountTagDetFacade';
import { ReturnCorporateComponent } from './views/return/return-corporate/return-corporate.component';
import { ReturnRequestComponent } from './views/return/return-request/return-request.component';
import { ReturnRequestDetailComponent } from './views/return/return-request/return-request-detail/return-request-detail.component';
import { ReturnStoreComponent } from './views/return/return-store/return-store.component';
import { ReturnStoreDetailComponent } from './views/return/return-store/return-store-detail/return-store-detail.component';
import { StoreReportDetailComponent } from './views/store/store-report-detail/store-report-detail.component';
import { StoreReportComponent } from './views/store/store-report/store-report.component';
import { SingleClickDirective } from './common/util/single-click.directive';
import { LoaderStoreComponent } from './common/presentation/loader-store/loader-store.component';
import { ModuleAdminComponent } from './views/configuration/module-admin/module-admin.component';
import { HiddenMenuFacade } from './common/facade/HiddenMenuFacade';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { TranferConsignmentComponent } from './views/transfer/tranfer-consignment/tranfer-consignment.component';
import { RestockNewComponent } from './views/restock/restock-new/restock-new.component';
import { RestockNewService } from './common/facade/restock-new.service';

import { ImageCollectorComponent } from './views/image-collector/image-collector.component';
import { DragndropDirective } from './directives/dragndrop.directive';
import { ProgressComponent } from './views/progress/progress.component';
import { RestockReportComponent } from './views/restock/restock-report/restock-report.component';
import { RestockReportService } from './common/facade/restock-report.service';
import { PreIronedFacade } from './common/facade/PreIronedFacade';
import { InventoryPreironedComponent } from './views/inventory/inventory-preironed/inventory-preironed.component';
import { ComboMultiSelectComponent } from './containers/combo-multi-select/combo-multi-select.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { TransferConsigmentFacade } from './common/facade/TransferConsigmentFacade';
import { PriceLabelComponent } from './views/configuration/price-label/price-label.component';
import { PriceLabelSection } from './common/model/PriceLabelSection';
import { PriceLabelFacade } from './common/facade/PriceLabelFacade';
import { ReturnRequestCorporateFacade } from './common/facade/ReturnRequestCorporateFacade';
import { OperationalLogFacade } from './common/facade/OperationalLogFacade';
import { ReturnStoreCorporateFacade } from './common/facade/ReturnStoreCorporateFacade';
import { InvoicementDownloadComponent } from './views/inventory/invoicement-download/invoicement-download.component';
import { InvoicementDownloadFacade } from './common/facade/InvoicementDownloadFacade';
import { LoggerLayoutsComponent } from './views/configuration/logger-layouts/logger-layouts.component';
import { LoggerLayoutsFacade } from './common/facade/LoggerLayoutsFacade';
import { StoreReportChecklistComponent } from './views/store/store-report-checklist/store-report-checklist.component';
import { UsersAdminComponent } from './views/configuration/admin-users/users-admin/users-admin.component';
import { UserModalComponent } from './views/configuration/admin-users/user-modal/user-modal.component';
import { CredentialStatusPipe } from './common/pipes/credential-status.pipe';
import { UserRolesModalComponent } from './views/configuration/admin-users/user-roles-modal/user-roles-modal.component';
import { GenericConfirmDialogComponent } from './views/configuration/admin-users/generic-confirm-dialog/generic-confirm-dialog.component';
import { AdminRolesComponent } from './views/configuration/admin-roles/admin-roles/admin-roles.component';
import { RolePermissionsModalComponent } from './views/configuration/admin-roles/role-permissions-modal/role-permissions-modal.component';
import { UnsoldItemSizeComponent } from './views/inventory/unsold-item-size/unsold-item-size.component';

registerLocaleData(localeES, 'es');

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
    suppressScrollX: true
};

const APP_CONTAINERS = [
    DefaultLayoutComponent
];


@NgModule({
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA
    ],
    imports: [
        BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
        HttpClientModule,
        BrowserAnimationsModule,
        CommonModule,
        AppRoutingModule,
        AppAsideModule,
        AppBreadcrumbModule.forRoot(),
        AppFooterModule,
        AppHeaderModule,
        AppSidebarModule,
        PerfectScrollbarModule,
        BsDropdownModule.forRoot(),
        TabsModule.forRoot(),
        ChartsModule,
        FormsModule,
        ReactiveFormsModule,
        ModalModule.forRoot(),
        MatProgressSpinnerModule,
        MatRadioModule,
        MatSliderModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatInputModule,
        FormsModule,
        NgxDatatableModule,
        FontAwesomeModule,
        OwlModule,
        CarouselModule.forRoot(),
        AlertModule.forRoot(),
        CollapseModule.forRoot(),
        ImageViewerModule.forRoot(),
        DateValueAccessorModule,
        BsDatepickerModule.forRoot(),
        NgbModule,
        AngularDualListBoxModule,
        AutocompleteLibModule,
        PdfViewerModule,
        NgSelectModule

    ],
    declarations: [
        AppComponent,
        ...APP_CONTAINERS,
        P404Component,
        P500Component,
        LoginComponent,
        HomeComponent,
        ReportComponent,
        ReportStoreComponent,
        ReportStoreInstructionComponent,
        ReportStorePriceChangeComponent,
        ReportStorePriceAdvanceComponent,
        ReportStoreInventoryComponent,
        LoaderComponent,
        HeaderNavComponent,
        AdminComponent,
        ChecklistComponent,
        OrganizationChartComponent,
        OrganizationChartDetailComponent,
        ReceptionsComponent,
        ReceptionsDetailComponent,
        ShipmentsComponent,
        shipmentsAsnDetailComponent,
        ShipmentsAsnVarietyDetailComponent,
        ShipmentsAsnComponent,
        InventoryEntryComponent,
        PackageReceptionComponent,
        StoresComponent,
        InventoryEntryDetComponent,
        EmployeeAdminComponent,
        DeviceAdminComponent,
        TemplateComponent,
        ActionDetailComponent,
        ActionStoreComponent,
        DateFormatPipe,
        TransferCorporateComponent,
        TransferStoreComponent,
        TransferCorporateDetailComponent,
        TransferStoreDetailComponent,
        TransferStoreEntryComponent,
        TransferStoreEntryDetailComponent,
        InventoryCountComponent,
        InventoryCountCreateComponent,
        InventoryCountDetailComponent,
        ReturnCorporateComponent,
        ReturnRequestComponent,
        ReturnRequestDetailComponent,
        ReturnStoreComponent,
        ReturnStoreDetailComponent,
        StoreReportDetailComponent,
        StoreReportComponent,
        ZoneAdminComponent,
        SingleClickDirective,
        LoaderStoreComponent,
        ModuleAdminComponent,
        TranferConsignmentComponent,
        RestockNewComponent,
        RestockReportComponent,
        ImageCollectorComponent,
        DragndropDirective,
        ProgressComponent,
        ComboMultiSelectComponent,
        PriceLabelComponent,
        InventoryPreironedComponent,
        InvoicementDownloadComponent,
        LoggerLayoutsComponent,
        StoreReportChecklistComponent,
        UsersAdminComponent,
        UserModalComponent,
        CredentialStatusPipe,
        UserRolesModalComponent,
        GenericConfirmDialogComponent,
        AdminRolesComponent,
        RolePermissionsModalComponent,
        UnsoldItemSizeComponent
    ],
    providers: [
        Strings
        , ActivityStoreFacade
        , CatalogDetFacade
        , BlobStorageFacade
        , EmployeeFacade
        , StoreFacade
        , ChecklistFacade
        , ApplicationFacade
        , StoreScheduleFacade
        , ConfigurationFacade
        , DashboardFacade
        , DeviceFacade
        , PreIronedFacade
        , LoggerLayoutsFacade
        , ActivityStoreInstructionActionFacade
        , InvoicementDownloadFacade
        , AppInfo
        , DatePipe
        , MatDatepickerModule
        , { provide: LOCALE_ID, useValue: 'es' }
        , {
            provide: LocationStrategy
            , useClass: HashLocationStrategy
        }
        , SecurityFacade
        , PositionFacade
        , PositionStoreFacade
        , ShipmentVarietySizeFacade
        , ShipmentManifestFacade
        , ShipmentAsnService
        , ShipmentVarietySizeASNService
        , InventoryEntryDetFacade
        , {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptorService,
            multi: true
        },
        PackageReceptionFacade,
        ShipmentFacade,
        ExcelUtility,
        DateFormatPipe,
        TransferRequestFacade,
        OrganizationFacade,
        TransferStoreFacade,
        InventoryCountFacade,
        InventoryCountTagDetFacade,
        ReturnRequestFacade,
        ReturnStoreFacade,
        CampaignCalendarFacade,
        RegionalStoreStructureFacade,
        ReturnConfigFacade,
        HiddenMenuFacade,
        ReturnRequestCorporateFacade,
        ReturnStoreCorporateFacade,
        OperationalLogFacade,
        DecimalPipe,
        RestockNewService,
        RestockReportService,
        ImageCollectorFacade,
        TenantFacade,
        TransferConsigmentFacade,
        PriceLabelSectionFacade,
        PriceLabelFacade,
        TaskFacade,
        UnsoldItemSizeFacade,
        TransferRequestBulkFacade,
        TransferBulkSignalRService,
        WhatsNewFacade
    ],
    entryComponents: [
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
